import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

interface UseBreederProfilesProps {
  searchTerm?: string;
  selectedBreed?: string;
  selectedLocation?: string;
  locationCoordinates?: [number, number] | null;
  rangeKm?: number;
  locationType?: string | null;
  page?: number;
  limit?: number;
}

// Function to calculate distance between two coordinates using Haversine formula
const calculateDistance = (
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number
): number => {
  const R = 6371; // Radius of the Earth in kilometers
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const distance = R * c; // Distance in kilometers
  return distance;
};

export const useBreederProfiles = ({
  searchTerm = "",
  selectedBreed = "",
  selectedLocation = "",
  locationCoordinates = null,
  rangeKm = 50,
  locationType = null,
  page = 1,
  limit = 12,
}: UseBreederProfilesProps) => {
  return useQuery({
    queryKey: [
      "breeder-profiles",
      searchTerm,
      selectedBreed,
      selectedLocation,
      locationCoordinates,
      rangeKm,
      locationType,
      page,
    ],
    queryFn: async () => {
      // First get all breeder profiles
      let query = supabase
        .from("profiles")
        .select(
          `
          id,
          business_name,
          first_name,
          last_name,
          location,
          profile_image_url,
          cover_image_url,
          description,
          location_coordinates,
          breeds,
          admin_tier,
          subscription_active
        `,
          { count: "exact" }
        )
        .eq("user_type", "breeder")
        .order("created_at", { ascending: false });

      // Apply filters
      if (searchTerm) {
        query = query.or(
          `business_name.ilike.%${searchTerm}%,first_name.ilike.%${searchTerm}%,last_name.ilike.%${searchTerm}%`
        );
      }

      const { data: profilesData, error: profilesError, count } = await query;

      if (profilesError) {
        console.error("Error fetching breeder profiles:", profilesError);
        throw profilesError;
      }

      if (!profilesData || profilesData.length === 0) {
        console.log("No breeder profiles found");
        return { profiles: [], totalCount: count || 0 };
      }

      // Filter profiles to only include those with active subscriptions OR freemium admin tier
      const filteredBySubscription = profilesData.filter((profile) => {
        // Check if user has active subscription OR freemium admin tier
        const hasActiveSubscription = profile.subscription_active === true;
        const hasFreemiumTier = profile.admin_tier === "freemium";

        console.log(
          `Profile ${profile.id}: subscription_active=${hasActiveSubscription}, admin_tier=${profile.admin_tier}, freemium=${hasFreemiumTier}`
        );

        return hasActiveSubscription || hasFreemiumTier;
      });

      // Filter by breed if selected
      let filteredData = filteredBySubscription;
      if (selectedBreed) {
        filteredData = filteredBySubscription.filter((profile) => {
          if (!profile.breeds) return false;

          try {
            const breeds = Array.isArray(profile.breeds) ? profile.breeds : [];
            return breeds.some(
              (breed) =>
                typeof breed === "string" &&
                breed.toLowerCase() === selectedBreed.toLowerCase()
            );
          } catch (e) {
            console.error(`Error parsing breeds for profile ${profile.id}:`, e);
            return false;
          }
        });
      }

      // Filter by location
      if (selectedLocation) {
        const isBroadArea = locationType === 'country' || locationType === 'region';
        
        if (isBroadArea) {
          // For country/region: text-based filtering
          const locationParts = selectedLocation.split(',').map(p => p.trim().toLowerCase());
          filteredData = filteredData.filter((profile) => {
            if (!profile.location) return false;
            const profileLoc = profile.location.toLowerCase();
            return locationParts.some(part => profileLoc.includes(part));
          });
        } else if (locationCoordinates) {
          // For city/locality: coordinate + distance filtering
          const [searchLng, searchLat] = locationCoordinates;

          filteredData = filteredData.filter((profile) => {
            if (!profile.location_coordinates) return false;

            try {
              let profileLng: number, profileLat: number;

              if (typeof profile.location_coordinates === "string") {
                const coords = JSON.parse(profile.location_coordinates);
                profileLng = coords.longitude;
                profileLat = coords.latitude;
              } else {
                const coords = profile.location_coordinates as any;
                if (coords.longitude && coords.latitude) {
                  profileLng = coords.longitude;
                  profileLat = coords.latitude;
                } else {
                  return false;
                }
              }

              const distance = calculateDistance(searchLat, searchLng, profileLat, profileLng);
              return distance <= rangeKm;
            } catch (e) {
              return false;
            }
          });
        }
      }

      // Apply pagination to filtered data
      const totalFilteredCount = filteredData.length;
      const from = (page - 1) * limit;
      const to = from + limit;
      const paginatedData = filteredData.slice(from, to);

      // Transform the data to match the expected format
      const transformedData = paginatedData.map((profile) => {
        // Safely handle the breeds field
        const breedsArray =
          Array.isArray(profile.breeds) &&
          profile.breeds.every((breed) => typeof breed === "string")
            ? (profile.breeds as string[])
            : [];

        return {
          id: profile.id,
          name:
            profile.business_name ||
            `${profile.first_name || ""} ${profile.last_name || ""}`.trim() ||
            "Unknown Breeder",
          profileImage: profile.profile_image_url,
          coverImage: profile.cover_image_url,
          location: profile.location || "Location not specified",
          description: profile.description || "No description available.",
          breeds: breedsArray,
        };
      });

      return { profiles: transformedData, totalCount: totalFilteredCount };
    },
  });
};
